import React, { PropsWithChildren, useCallback, useEffect, useRef, useState } from "react";
import { AuthService } from "../../services/AuthService";
import { useUserSessionContext } from "../../contexts/UserSessionContext";
import { LoadingPage } from "../pages/LoadingPage";
import { LandingPage } from "../pages/LandingPage";
import { usePostRefreshToken, usePostAccessToken, useGetUserInfo } from "../../hooks";
import { useSearchParams } from "react-router";
import { UserSession } from "../../types";
import {
  ACCESS_TOKEN_EXPIRATION_MS,
  ACCESS_TOKEN_REFRESH_BACKOFF_MS,
  ACCESS_TOKEN_REFRESH_INTERVAL_MS,
} from "../../config/auth";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";

type AuthState = "idle" | "loginPage" | "loading";

export function Auth({ children }: PropsWithChildren) {
  const [authState, setAuthState] = useState<AuthState>("loading");
  let [searchParams] = useSearchParams();
  const code = useRef(searchParams.get("code"));
  const { userSession, setUserSession } = useUserSessionContext();
  const { fetch: fetchAccessToken } = usePostAccessToken();
  const { fetch: fetchRefreshToken } = usePostRefreshToken();
  const { fetch: fetchUserInfo } = useGetUserInfo();
  const { isAskGpcEnabled } = useFeatureFlags();

  const updateRefreshToken = useCallback(
    (data: UserSession) => {
      if (data) {
        AuthService.setRefreshTokenCookie(data.refreshToken);
        setUserSession((prev) => ({ ...(prev || {}), ...data }));
        setAuthState("idle");
      }
    },
    [setUserSession],
  );

  const getUserGroups = useCallback(
    async (accessToken: string) => {
      if (!isAskGpcEnabled) return [];
      const userInfoResponse = await fetchUserInfo({
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });
      return userInfoResponse?.data?.groups || [];
    },
    [fetchUserInfo, isAskGpcEnabled],
  );

  const handleRefetchRefreshToken = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!silent) setAuthState("loading");
      try {
        const tokensResponse = await fetchRefreshToken({
          body: { refresh_token: AuthService.getRefreshTokenCookie() },
        });

        if (!tokensResponse?.data) return;

        updateRefreshToken({ ...tokensResponse.data, groups: await getUserGroups(tokensResponse.data.accessToken) });
      } catch (error) {
        AuthService.unsetRefreshTokenCookie();
        setUserSession(null);
        setAuthState("loginPage");
      }
    },
    [fetchRefreshToken, setUserSession, updateRefreshToken, getUserGroups],
  );

  useEffect(() => {
    if (code.current) {
      window.history.replaceState({}, "", window.location.pathname);
      setAuthState("loading");
      (async () => {
        try {
          const tokensResponse = await fetchAccessToken({
            body: { code: code.current, redirect_uri: process.env.REACT_APP_BASE_URL },
          });

          if (!tokensResponse?.data) return;

          updateRefreshToken({ ...tokensResponse.data, groups: await getUserGroups(tokensResponse.data.accessToken) });
        } finally {
          code.current = "";
        }
      })();
    } else {
      const refresh_token = AuthService.getRefreshTokenCookie();
      if (refresh_token) {
        handleRefetchRefreshToken();
      } else {
        setAuthState("loginPage");
      }
    }
  }, [code, fetchAccessToken, handleRefetchRefreshToken, updateRefreshToken, getUserGroups]);

  useEffect(() => {
    setAuthState((prevState) => {
      if (prevState === "idle" && userSession === null) {
        return "loginPage";
      }
      return prevState;
    });
  }, [userSession]);

  useEffect(() => {
    if (userSession?.accessToken === "" && !!userSession?.refreshToken) handleRefetchRefreshToken({ silent: true });
  }, [userSession?.accessToken, userSession?.refreshToken, handleRefetchRefreshToken]);

  useEffect(() => {
    const clearAccessToken = () =>
      setUserSession((prev) => {
        if (!prev?.expiresAt) return prev;

        // Skip refreshing if token was recently renewed
        if (new Date().getTime() < prev.expiresAt - ACCESS_TOKEN_EXPIRATION_MS + ACCESS_TOKEN_REFRESH_BACKOFF_MS)
          return prev;

        return { ...prev, accessToken: "" };
      });

    window.addEventListener("focus", clearAccessToken);
    const refreshInterval = setInterval(clearAccessToken, ACCESS_TOKEN_REFRESH_INTERVAL_MS);

    return () => {
      window.removeEventListener("focus", clearAccessToken);
      clearInterval(refreshInterval);
    };
  }, [setUserSession]);

  if (authState === "loginPage") {
    return <LandingPage />;
  }

  if (authState === "loading") {
    return <LoadingPage />;
  }

  return <>{children}</>;
}
